"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useUser, SignInButton } from "@clerk/nextjs";
import Feature from "@/components/Feature";

interface OrderItem {
  quantity: number;
  price: number;
  productTitle?: string;
  productImage?: string;
}

interface Order {
  _id: string;
  orderId?: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
  };
  items: OrderItem[];
  totalPrice: number;
  status: string;
  paymentStatus: string;
  paymentMethod: string;
  orderDate: string;
  deliveryAgent?: string;
  deliveryConfirmedAt?: string;
}

/* ── Status config ── */
const STATUS_META: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  pending:                  { label: "Pending",          color: "#92400e", bg: "#fef3c7", dot: "#f59e0b" },
  pending_payment:          { label: "Pending Payment",  color: "#92400e", bg: "#fef3c7", dot: "#f59e0b" },
  awaiting_bank_transfer:   { label: "Awaiting Transfer",color: "#5b21b6", bg: "#ede9fe", dot: "#7c3aed" },
  processing:               { label: "Processing",       color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  paid:                     { label: "Paid",             color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  confirmed:                { label: "Confirmed",        color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  shipped:                  { label: "Shipped",          color: "#4338ca", bg: "#e0e7ff", dot: "#6366f1" },
  dispatched:               { label: "Dispatched",       color: "#0f766e", bg: "#ccfbf1", dot: "#14b8a6" },
  out_for_delivery:         { label: "Out for Delivery", color: "#075985", bg: "#e0f2fe", dot: "#0ea5e9" },
  delivered:                { label: "Delivered",        color: "#065f46", bg: "#d1fae5", dot: "#22c55e" },
  completed:                { label: "Delivered",        color: "#065f46", bg: "#d1fae5", dot: "#22c55e" },
  cancelled:                { label: "Cancelled",        color: "#991b1b", bg: "#fee2e2", dot: "#ef4444" },
};

const PAYMENT_META: Record<string, { label: string; color: string; bg: string }> = {
  pending:   { label: "Unpaid",    color: "#92400e", bg: "#fef9c3" },
  completed: { label: "Paid ✓",   color: "#065f46", bg: "#d1fae5" },
  failed:    { label: "Failed",    color: "#991b1b", bg: "#fee2e2" },
};

export default function MyOrdersPage() {
  const { isSignedIn, isLoaded, user } = useUser();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (isSignedIn) fetchOrders();
  }, [isSignedIn]);

  const fetchOrders = async () => {
    try {
      const res = await fetch("/api/orders");
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  /* ── Loading ── */
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-[#B88E2F]" />
          <p className="text-sm text-gray-400 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  /* ── Not signed in ── */
  if (!isSignedIn) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f8f7f4] px-4">
        <div className="bg-white rounded-3xl shadow-xl p-10 max-w-sm w-full text-center border border-gray-100">
          <div className="w-20 h-20 bg-gradient-to-br from-[#fef3c7] to-[#fde68a] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-md">
            <svg className="w-10 h-10 text-[#B88E2F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-xl font-extrabold text-gray-900 mb-2 tracking-tight">Sign In Required</h1>
          <p className="text-gray-500 text-sm mb-7 leading-relaxed">
            Please sign in to view your complete order history and track deliveries.
          </p>
          <SignInButton mode="modal" forceRedirectUrl="/my-orders">
            <button className="w-full bg-gradient-to-r from-[#B88E2F] to-[#8C6D23] hover:from-[#8C6D23] hover:to-[#7a5c1e] text-white font-bold py-3.5 px-4 rounded-xl transition-all shadow-lg shadow-amber-200">
              Sign In to Continue
            </button>
          </SignInButton>
          <Link href="/track-order" className="block mt-4 text-sm text-[#B88E2F] font-semibold hover:underline">
            Or track without signing in →
          </Link>
        </div>
      </div>
    );
  }

  const filteredOrders = filter === "all"
    ? orders
    : orders.filter(o => o.status === filter || (filter === "delivered" && o.status === "completed"));

  const tabs = [
    { key: "all",       label: "All Orders" },
    { key: "pending",   label: "Pending" },
    { key: "shipped",   label: "Shipped" },
    { key: "delivered", label: "Delivered" },
    { key: "cancelled", label: "Cancelled" },
  ];

  return (
    <>
      <div className="min-h-screen bg-[#f8f7f4] pb-16">

        {/* ── Elegant Minimal Header ── */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-28">
          <div className="mb-8 text-left border-b border-gray-100 pb-5">
            <div className="flex items-center gap-2 text-xs font-bold tracking-wider text-[#B88E2F] uppercase mb-1">
              <Link href="/" className="hover:text-gray-950 transition-colors">Home</Link>
              <span className="text-gray-300">/</span>
              <span className="text-gray-400">My Orders</span>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                  My Orders
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-1.5 leading-relaxed">
                  {loading ? "Loading your orders..." : `${orders.length} ${orders.length === 1 ? "order" : "orders"} placed`}
                </p>
              </div>
              <Link
                href="/shop"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#B88E2F] hover:text-[#8C6D23] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">

          {/* ── Filter Tabs ── */}
          {!loading && orders.length > 0 && (
            <div className="flex gap-2 mb-6 overflow-x-auto pb-1 scrollbar-hide">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all ${
                    filter === tab.key
                      ? "bg-[#B88E2F] text-white shadow-md shadow-amber-200"
                      : "bg-white text-gray-500 hover:bg-gray-50 border border-gray-200"
                  }`}
                >
                  {tab.label}
                  {tab.key === "all" && (
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${filter === "all" ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                      {orders.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* ── Loading ── */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-[#B88E2F] mb-4" />
              <p className="text-sm text-gray-400 font-medium">Fetching your orders...</p>
            </div>

          /* ── Empty ── */
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
              <div className="w-20 h-20 bg-gradient-to-br from-[#fef3c7] to-[#fde68a] rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-md">
                <svg className="w-10 h-10 text-[#B88E2F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h2 className="text-lg font-extrabold text-gray-800 mb-2">
                {filter === "all" ? "No orders yet" : `No ${filter} orders`}
              </h2>
              <p className="text-gray-400 text-sm mb-7 max-w-xs mx-auto">
                {filter === "all"
                  ? "Start shopping to see your orders here!"
                  : "Try a different filter to see your orders."}
              </p>
              {filter === "all" ? (
                <Link href="/shop" className="inline-block bg-[#B88E2F] hover:bg-[#8C6D23] text-white font-bold py-3 px-8 rounded-xl transition-all shadow-lg shadow-amber-100">
                  Browse Products
                </Link>
              ) : (
                <button onClick={() => setFilter("all")} className="inline-block text-[#B88E2F] font-semibold text-sm hover:underline">
                  View all orders
                </button>
              )}
            </div>

          /* ── Orders List ── */
          ) : (
            <div className="space-y-3">
              {filteredOrders.map((order) => {
                const status = STATUS_META[order.status] || { label: order.status, color: "#374151", bg: "#f3f4f6", dot: "#9ca3af" };
                const payment = PAYMENT_META[order.paymentStatus] || { label: order.paymentStatus, color: "#374151", bg: "#f3f4f6" };

                return (
                  <Link
                    key={order._id}
                    href={`/my-orders/${order._id}`}
                    className="group block bg-white rounded-2xl border border-gray-100 hover:border-[#B88E2F]/30 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                  >
                    {/* Top accent bar based on status */}
                    <div className="h-1 w-full" style={{ background: status.dot }} />

                    <div className="px-5 py-4 sm:px-6 sm:py-5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">

                        {/* Left — order info */}
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: status.bg }}>
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke={status.dot} strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-extrabold text-gray-900 tracking-tight">
                              {order.orderId || `Order #${order._id.slice(-8).toUpperCase()}`}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {new Date(order.orderDate).toLocaleDateString("en-PK", {
                                year: "numeric", month: "short", day: "numeric",
                              })}
                              {" · "}
                              {order.items?.length || 0} {(order.items?.length || 0) === 1 ? "item" : "items"}
                              {" · "}
                              <span className="capitalize">{order.paymentMethod || "COD"}</span>
                            </p>
                          </div>
                        </div>

                        {/* Right — badges + price + arrow */}
                        <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                          {/* Status badge */}
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold" style={{ color: status.color, background: status.bg }}>
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: status.dot }} />
                            {status.label}
                          </span>
                          {/* Payment badge */}
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold" style={{ color: payment.color, background: payment.bg }}>
                            {payment.label}
                          </span>
                          {/* Price */}
                          <span className="text-base font-extrabold text-gray-900 ml-1">
                            Rs. {order.totalPrice?.toLocaleString()}
                          </span>
                          {/* Arrow */}
                          <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#B88E2F] flex items-center justify-center transition-colors flex-shrink-0">
                            <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Delivered confirmation strip */}
                      {order.deliveryConfirmedAt && (
                        <div className="mt-3 px-3 py-2 bg-green-50 rounded-xl border border-green-100 flex items-center gap-2">
                          <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          <p className="text-xs font-semibold text-green-700">
                            Delivered on {new Date(order.deliveryConfirmedAt).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" })}
                          </p>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* ── Quick Track CTA ── */}
          {!loading && orders.length > 0 && (
            <div className="mt-8 bg-gradient-to-r from-gray-900 to-[#1c1208] rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#B88E2F]/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#B88E2F]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Need to track a specific order?</p>
                  <p className="text-xs text-gray-400">Use our public tracker — no sign in needed</p>
                </div>
              </div>
              <Link
                href="/track-order"
                className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-[#B88E2F] hover:bg-[#8C6D23] text-white text-sm font-bold rounded-xl transition-all shadow-lg shadow-amber-900/20"
              >
                Track Order
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            </div>
          )}

        </div>
      </div>

      <Feature />
    </>
  );
}
